import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
export { untilDestroyed } from '@ngneat/until-destroy';
import * as i0 from '@angular/core';
import { EventEmitter, inject, ElementRef, input, HostListener, Directive, ViewContainerRef, TemplateRef, output, Renderer2, NgZone, PLATFORM_ID, ContentChildren } from '@angular/core';
import { takeUntil, debounceTime, first, catchError, filter, tap, map, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { merge, fromEvent, of, Subject } from 'rxjs';
import { PlatformService } from 'ngx-unificator/services';
import { DeviceDetectorService } from 'ngx-device-detector';
import { __decorate } from 'tslib';
import { NgControl } from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { RouterLinkActive } from '@angular/router';
let AccordionDirective = /*#__PURE__*/(() => {
  class AccordionDirective {
    /**
     * Listen accordion click
     */
    onClick() {
      this.toggle();
    }
    constructor() {
      /**
       * Emits when component destroyed
       */
      this._alive$ = new EventEmitter();
      this.obg = {
        a: '1',
        b: '2'
      };
      /**
       * Is accordion opened
       */
      this._open = false;
      /**
       * Accordion content element
       */
      this._content = null;
      this._el = inject(ElementRef);
      this._platform = inject(PlatformService);
      this.hideOnHeightLess = input(false, ...(ngDevMode ? [{
        debugName: "hideOnHeightLess"
      }] : []));
      /**
       * Css defined Max height of content
       */
      this.maxHeight = input(null, ...(ngDevMode ? [{
        debugName: "maxHeight"
      }] : []));
    }
    get isOpen() {
      return this._open;
    }
    ngAfterViewInit() {
      this._button = this._el.nativeElement;
      // Getting the next sibling of the element and ignore html comment
      let nextSibling = this._el.nativeElement.nextSibling;
      while (nextSibling) {
        if (nextSibling.nodeType !== 8) {
          break;
        }
        nextSibling = nextSibling.nextSibling;
      }
      this._content = nextSibling;
      this._resolveInitialState();
      this._listenRecalcHeight();
    }
    /**
     * Call once when component destroyed
     * Clear listeners
     */
    ngOnDestroy() {
      this._alive$.next(false);
      this._alive$.complete();
    }
    /**
     * Toggle accordion
     */
    toggle() {
      if (this._open) {
        this.close();
      } else {
        this.open();
      }
    }
    /**
     * Open accordion
     */
    open() {
      if (!this._content || !this._button) {
        return;
      }
      this._open = true;
      this._content.style.maxHeight = this._content.scrollHeight + 'px';
      this._button.classList.add('open');
      this._content.classList.add('open');
    }
    /**
     * Close accordion
     */
    close() {
      if (!this._content || !this._button) {
        return;
      }
      this._open = false;
      this._content.style.maxHeight = null;
      this._button.classList.remove('open');
      this._content.classList.remove('open');
    }
    /**
     * Resolve accordion initial state
     *
     * @private
     */
    _resolveInitialState() {
      if (this._button.classList.contains('open')) {
        this.open();
      } else {
        this.close();
      }
    }
    /**
     * If scroll height of content less then initial height - hide accordion button
     *
     * @private
     */
    _checkLessHeight() {
      this._button.hidden = this.maxHeight() >= this._content.offsetHeight && this._content.offsetHeight === this._content.scrollHeight;
    }
    /**
     * Update opened accordion height on 'AccordionResize' event
     *
     * @private
     */
    _listenRecalcHeight() {
      if (this._platform.isBrowser) {
        merge(fromEvent(window, 'AccordionResize'), fromEvent(window, 'resize')).pipe(takeUntil(this._alive$), debounceTime(200)).subscribe(() => {
          if (this._open) {
            this.open();
          }
          if (this.hideOnHeightLess()) {
            setTimeout(() => this._checkLessHeight(), 100);
          }
        });
      }
    }
    static {
      this.ɵfac = function AccordionDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AccordionDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AccordionDirective,
        selectors: [["", "Accordion", ""]],
        hostBindings: function AccordionDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function AccordionDirective_click_HostBindingHandler() {
              return ctx.onClick();
            });
          }
        },
        inputs: {
          hideOnHeightLess: [1, "hideOnHeightLess"],
          maxHeight: [1, "maxHeight"]
        },
        exportAs: ["accordionDirective"]
      });
    }
  }
  return AccordionDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AppShellMobileDirective = /*#__PURE__*/(() => {
  class AppShellMobileDirective {
    constructor() {
      this._viewContainer = inject(ViewContainerRef);
      this._templateRef = inject(TemplateRef);
      this._device = inject(DeviceDetectorService);
    }
    ngOnInit() {
      if (this._device.isMobile()) {
        this._viewContainer.createEmbeddedView(this._templateRef);
      } else {
        this._viewContainer.clear();
      }
    }
    static {
      this.ɵfac = function AppShellMobileDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AppShellMobileDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AppShellMobileDirective,
        selectors: [["", "appShellMobile", ""]]
      });
    }
  }
  return AppShellMobileDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AppShellNoMobileDirective = /*#__PURE__*/(() => {
  class AppShellNoMobileDirective {
    constructor() {
      this._viewContainer = inject(ViewContainerRef);
      this._templateRef = inject(TemplateRef);
      this._device = inject(DeviceDetectorService);
    }
    ngOnInit() {
      if (!this._device.isMobile()) {
        this._viewContainer.createEmbeddedView(this._templateRef);
      } else {
        this._viewContainer.clear();
      }
    }
    static {
      this.ɵfac = function AppShellNoMobileDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AppShellNoMobileDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AppShellNoMobileDirective,
        selectors: [["", "appShellNoMobile", ""]]
      });
    }
  }
  return AppShellNoMobileDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AppShellNoSSRDirective = /*#__PURE__*/(() => {
  class AppShellNoSSRDirective {
    constructor() {
      this._viewContainer = inject(ViewContainerRef);
      this._templateRef = inject(TemplateRef);
      this._platform = inject(PlatformService);
    }
    ngOnInit() {
      if (this._platform.isBrowser) {
        this._viewContainer.createEmbeddedView(this._templateRef);
      } else {
        this._viewContainer.clear();
      }
    }
    static {
      this.ɵfac = function AppShellNoSSRDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AppShellNoSSRDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AppShellNoSSRDirective,
        selectors: [["", "appShellNoSSR", ""]]
      });
    }
  }
  return AppShellNoSSRDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AppShellRenderSSRDirective = /*#__PURE__*/(() => {
  class AppShellRenderSSRDirective {
    constructor() {
      this._viewContainer = inject(ViewContainerRef);
      this._templateRef = inject(TemplateRef);
      this._platform = inject(PlatformService);
    }
    ngOnInit() {
      if (!this._platform.isBrowser) {
        this._viewContainer.createEmbeddedView(this._templateRef);
      } else {
        this._viewContainer.clear();
      }
    }
    static {
      this.ɵfac = function AppShellRenderSSRDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AppShellRenderSSRDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AppShellRenderSSRDirective,
        selectors: [["", "appShellRenderSSR", ""]]
      });
    }
  }
  return AppShellRenderSSRDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ClassLoadedDirective = /*#__PURE__*/(() => {
  let ClassLoadedDirective = class ClassLoadedDirective {
    constructor() {
      this._el = inject(ElementRef);
      this._platform = inject(PlatformService);
    }
    ngAfterViewInit() {
      fromEvent(this._el.nativeElement, 'load').pipe(untilDestroyed(this), first(), catchError(error => of(error)), filter(() => this._platform.isBrowser), tap(() => {
        this._el.nativeElement.classList.add('loaded');
      })).subscribe();
    }
    static {
      this.ɵfac = function ClassLoadedDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ClassLoadedDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ClassLoadedDirective,
        selectors: [["", "ClassLoaded", ""]]
      });
    }
  };
  ClassLoadedDirective = __decorate([UntilDestroy()], ClassLoadedDirective);
  return ClassLoadedDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ClickOutsideDirective = /*#__PURE__*/(() => {
  class ClickOutsideDirective {
    clickedOutside(target) {
      if (this._isClickedInside(target)) {
        this.clickInside.emit();
      } else {
        this.clickOutside.emit();
      }
    }
    constructor() {
      this._el = inject(ElementRef);
      this.options = input({
        ignoreOutsideComponents: []
      }, ...(ngDevMode ? [{
        debugName: "options"
      }] : []));
      this.clickOutside = output();
      this.clickInside = output();
    }
    /**
     * Check if a click was inside the element
     *
     * @param target
     * @private
     */
    _isClickedInside(target) {
      if (this._el.nativeElement.contains(target)) {
        return true;
      } else {
        return this._isIgnoreOutsideElementsClicked(target);
      }
    }
    /**
     * Check if clicked element inside ignored elements
     *
     * @param target
     * @private
     */
    _isIgnoreOutsideElementsClicked(target) {
      return this.options().ignoreOutsideComponents.some(selector => target.closest(selector));
    }
    static {
      this.ɵfac = function ClickOutsideDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ClickOutsideDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ClickOutsideDirective,
        selectors: [["", "ClickOutside", ""]],
        hostBindings: function ClickOutsideDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("mousedown", function ClickOutsideDirective_mousedown_HostBindingHandler($event) {
              return ctx.clickedOutside($event.target);
            }, i0.ɵɵresolveDocument);
          }
        },
        inputs: {
          options: [1, "options"]
        },
        outputs: {
          clickOutside: "clickOutside",
          clickInside: "clickInside"
        },
        exportAs: ["clickOutsideDirective"]
      });
    }
  }
  return ClickOutsideDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CropTextDirective = /*#__PURE__*/(() => {
  class CropTextDirective {
    constructor() {
      /**
       * Source element text
       */
      this._sourceText = '';
      /**
       * Text after cropping
       */
      this._trimmedText = '';
      /**
       * Symbols displayed after cropped text
       */
      this._endSymbols = '...';
      /**
       * More element btn
       */
      this._moreEl = null;
      this._alive$ = new EventEmitter();
      this._el = inject(ElementRef);
      /**
       * Symbols count needed to display in cropped text
       */
      this.symbolsCount = input(50, ...(ngDevMode ? [{
        debugName: "symbolsCount"
      }] : []));
    }
    ngOnDestroy() {
      this._alive$.next(false);
      this._alive$.complete();
    }
    ngAfterViewInit() {
      this._resolveMoreBtn();
      this._cropText();
      if (!this._trimmedText) {
        return; // exit if source text length less then symbolsCount
      }
      this._el.nativeElement.innerText = this._trimmedText;
      if (this._moreEl) {
        this._el.nativeElement.append(this._moreEl);
      }
    }
    /**
     * Crop text in element if it longer then symbolsCount
     *
     * @private
     */
    _cropText() {
      this._sourceText = this._el.nativeElement.innerText;
      // trim and re-trim only when necessary (prevent re-trim when string is shorted than maxLength, it causes last word cut)
      if (this._sourceText.length > this.symbolsCount()) {
        // trim the string to the maximum length
        let trimmedString = this._sourceText.substr(0, this.symbolsCount());
        // re-trim if we are in the middle of a word and
        trimmedString = trimmedString.substr(0, Math.min(trimmedString.length, trimmedString.lastIndexOf(' ')));
        this._trimmedText = trimmedString + this._endSymbols;
      }
    }
    /**
     * Create more btn based on element with data-cropText-more attribute in view
     *
     * @private
     */
    _resolveMoreBtn() {
      const more = this._el.nativeElement.querySelector('[data-cropText-more]');
      if (!more) {
        return;
      }
      this._moreEl = more.cloneNode(true);
      more.remove();
      this._handleMoreClick();
    }
    /**
     * Handle more btn click
     *
     * @private
     */
    _handleMoreClick() {
      fromEvent(this._moreEl, 'click').pipe(first(), takeUntil(this._alive$)).subscribe(() => {
        this._el.nativeElement.innerText = this._sourceText;
        this._moreEl.remove();
      });
    }
    static {
      this.ɵfac = function CropTextDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CropTextDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: CropTextDirective,
        selectors: [["", "CropText", ""]],
        inputs: {
          symbolsCount: [1, "symbolsCount"]
        }
      });
    }
  }
  return CropTextDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DefaultSourceDirective = /*#__PURE__*/(() => {
  let DefaultSourceDirective = class DefaultSourceDirective {
    constructor() {
      this._el = inject(ElementRef);
      this._platform = inject(PlatformService);
      this._renderer = inject(Renderer2);
      this.source = input('', ...(ngDevMode ? [{
        debugName: "source"
      }] : []));
    }
    ngAfterViewInit() {
      if (!this.source()) {
        throw new Error('You should provide [source] attribute');
      }
      fromEvent(this._el.nativeElement, 'error').pipe(untilDestroyed(this), first(), filter(() => this._platform.isBrowser), tap(() => {
        this._el && this._el.nativeElement && this._renderer.setAttribute(this._el.nativeElement, 'src', this.source());
      })).subscribe();
    }
    static {
      this.ɵfac = function DefaultSourceDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DefaultSourceDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultSourceDirective,
        selectors: [["", "defaultSource", ""]],
        inputs: {
          source: [1, "source"]
        }
      });
    }
  };
  DefaultSourceDirective = __decorate([UntilDestroy()], DefaultSourceDirective);
  return DefaultSourceDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Remove target element if image not exists
 * If target element not 'img' - for checking will be used first inner 'img'
 */
let DeleteNotFoundDirective = /*#__PURE__*/(() => {
  let DeleteNotFoundDirective = class DeleteNotFoundDirective {
    constructor() {
      this._el = inject(ElementRef);
      this._platform = inject(PlatformService);
      /**
       * Target image element that will be checked
       */
      this._target = null;
    }
    ngAfterViewInit() {
      this._target = this._el.nativeElement instanceof HTMLImageElement ? this._el.nativeElement : this._el.nativeElement.querySelector('img');
      if (!this._target) {
        return;
      }
      fromEvent(this._target, 'error').pipe(untilDestroyed(this), first(), catchError(error => of(error)), filter(() => this._platform.isBrowser), tap(() => {
        this._el.nativeElement.remove();
      })).subscribe();
    }
    static {
      this.ɵfac = function DeleteNotFoundDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DeleteNotFoundDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DeleteNotFoundDirective,
        selectors: [["", "DeleteNotFound", ""]]
      });
    }
  };
  DeleteNotFoundDirective = __decorate([UntilDestroy()], DeleteNotFoundDirective);
  return DeleteNotFoundDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FilterInputDirective = /*#__PURE__*/(() => {
  class FilterInputDirective {
    constructor() {
      this._control = inject(NgControl);
      this.filter = input('', ...(ngDevMode ? [{
        debugName: "filter"
      }] : []));
    }
    ngOnInit() {
      if (!this._control) {
        throw new Error('You should specify formControlName on your input element!');
      }
      const filterValue = this.filter();
      if (!filterValue) {
        throw new Error('You should provide [filter] string of unacceptable chars');
      }
      const regex = new RegExp(`[${filterValue}]`, 'g');
      /**
       * Remove unacceptable characters from input
       */
      this._control?.control?.valueChanges.pipe(map(value => [value, value.replace(regex, '')]), filter(([prev, current]) => prev !== current), tap(([prev, current]) => this._control?.control?.setValue(current, {
        emitEvent: false
      }))).subscribe();
    }
    static {
      this.ɵfac = function FilterInputDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FilterInputDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FilterInputDirective,
        selectors: [["", "filterInput", ""]],
        inputs: {
          filter: [1, "filter"]
        }
      });
    }
  }
  return FilterInputDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FitSvgDirective = /*#__PURE__*/(() => {
  class FitSvgDirective {
    constructor() {
      this._el = inject(ElementRef);
      this._platform = inject(PlatformService);
    }
    ngAfterContentInit() {
      if (this._platform.isBrowser) {
        setTimeout(() => {
          const bbox = this._el.nativeElement.querySelector('use').getBBox();
          if (bbox.width === 0 || bbox.height === 0) {
            this._el.nativeElement.remove();
          } else {
            this._el.nativeElement.setAttribute('viewBox', `0 0 ${bbox.width} ${bbox.height}`);
          }
        });
      }
    }
    static {
      this.ɵfac = function FitSvgDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FitSvgDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: FitSvgDirective,
        selectors: [["", "FitSvg", ""]]
      });
    }
  }
  return FitSvgDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ImgDimensionDirective = /*#__PURE__*/(() => {
  let ImgDimensionDirective = class ImgDimensionDirective {
    constructor() {
      this._el = inject(ElementRef);
      this._platform = inject(PlatformService);
      this.height = input(25, ...(ngDevMode ? [{
        debugName: "height"
      }] : []));
    }
    ngAfterViewInit() {
      fromEvent(this._el.nativeElement, 'load').pipe(untilDestroyed(this), first(), catchError(error => of(error)), filter(() => this._platform.isBrowser), tap(el => {
        this._el.nativeElement.height = this.height();
        this._el.nativeElement.width = this._resolveImgWidth(this._el.nativeElement.naturalHeight, this._el.nativeElement.naturalWidth);
      })).subscribe();
    }
    _resolveImgWidth(naturalHeight, naturalWidth) {
      return naturalWidth * Number(this.height()) / naturalHeight;
    }
    static {
      this.ɵfac = function ImgDimensionDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ImgDimensionDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ImgDimensionDirective,
        selectors: [["", "ImgDimension", ""]],
        inputs: {
          height: [1, "height"]
        }
      });
    }
  };
  ImgDimensionDirective = __decorate([UntilDestroy()], ImgDimensionDirective);
  return ImgDimensionDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let InputNumberDirective = /*#__PURE__*/(() => {
  class InputNumberDirective {
    constructor() {
      this._el = inject(ElementRef);
      this.maxlength = input(Infinity, ...(ngDevMode ? [{
        debugName: "maxlength"
      }] : []));
    }
    ngAfterViewInit() {
      const regEx = new RegExp('[0-9,.]');
      this._el.nativeElement.addEventListener('keypress', event => {
        const char = event.key !== undefined ? event.key : String.fromCharCode(event.keyCode);
        if (!regEx.test(char) || event.target.value.length > this.maxlength()) {
          event.preventDefault();
        }
      });
    }
    static {
      this.ɵfac = function InputNumberDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || InputNumberDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: InputNumberDirective,
        selectors: [["", "InputNumber", ""]],
        inputs: {
          maxlength: [1, "maxlength"]
        }
      });
    }
  }
  return InputNumberDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LeadingStringDirective = /*#__PURE__*/(() => {
  class LeadingStringDirective {
    constructor() {
      this._control = inject(NgControl);
      this.leading = input('', ...(ngDevMode ? [{
        debugName: "leading"
      }] : []));
    }
    ngOnInit() {
      if (!this._control) {
        throw new Error('You should specify formControlName on your input element!');
      }
      if (!this.leading()) {
        throw new Error('You should provide [leading] string');
      }
      /**
       * Add leading string if not exist
       */
      this._control?.control?.valueChanges.pipe(filter(value => value.indexOf(this.leading(), 0) !== 0), tap(value => this._control?.control?.setValue(this.leading() + value, {
        emitEvent: false
      }))).subscribe();
    }
    static {
      this.ɵfac = function LeadingStringDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LeadingStringDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LeadingStringDirective,
        selectors: [["", "LeadingString", ""]],
        inputs: {
          leading: [1, "leading"]
        }
      });
    }
  }
  return LeadingStringDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Angular Lazy Loading Images Directive
 *
 * The library allows to lazy load images from your web application
 * using the MutationObserver and the IntersectionObserver. Images will be loaded as
 * soon as they enter the viewport in a non-blocking way.
 */
let LazyLoadImagesDirective = /*#__PURE__*/(() => {
  class LazyLoadImagesDirective {
    constructor() {
      this.element = inject(ElementRef);
      this.renderer = inject(Renderer2);
      this.ngZone = inject(NgZone);
      this.platformId = inject(PLATFORM_ID);
      this.intersectionObserverConfig = input(undefined, ...(ngDevMode ? [{
        debugName: "intersectionObserverConfig",
        alias: "lazy-load-images"
      }] : [{
        alias: "lazy-load-images"
      }]));
      this.rootElement = this.element.nativeElement;
    }
    init() {
      this.registerIntersectionObserver();
      this.observeDOMChanges(this.rootElement, () => {
        const imagesFoundInDOM = this.getAllImagesToLazyLoad(this.rootElement);
        imagesFoundInDOM.forEach(image => this.intersectionObserver.observe(image));
      });
    }
    ngOnInit() {
      if (!this.isBrowser()) {
        return;
      }
      this.ngZone.runOutsideAngular(() => this.init());
    }
    ngOnDestroy() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    }
    isBrowser() {
      return isPlatformBrowser(this.platformId);
    }
    registerIntersectionObserver() {
      const intersectionObserverConfig = this.intersectionObserverConfig();
      this.intersectionObserver = new IntersectionObserver(images => images.forEach(image => this.onIntersectionChange(image)), intersectionObserverConfig instanceof Object ? intersectionObserverConfig : undefined);
      return this.intersectionObserver;
    }
    // tslint:disable-next-line:ban-types
    observeDOMChanges(rootElement, onChange) {
      // Create a Mutation Observer instance
      const observer = new MutationObserver(mutations => onChange(mutations));
      // Observer Configuration
      const observerConfig = {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true
      };
      // Observe Directive DOM Node
      observer.observe(rootElement, observerConfig);
      // Fire onChange callback to check current DOM nodes
      onChange();
      return observer;
    }
    getAllImagesToLazyLoad(pageNode) {
      return Array.from(pageNode.querySelectorAll('img[data-src], [data-srcset], [data-background-src]'));
    }
    onIntersectionChange(image) {
      if (!image.isIntersecting) {
        return;
      }
      this.onImageAppearsInViewport(image.target);
    }
    onImageAppearsInViewport(image) {
      if (image.dataset.src) {
        this.renderer.setAttribute(image, 'src', image.dataset.src);
        this.renderer.removeAttribute(image, 'data-src');
      }
      if (image.dataset.srcset) {
        this.renderer.setAttribute(image, 'srcset', image.dataset.srcset);
        this.renderer.removeAttribute(image, 'data-srcset');
      }
      if (image.dataset.backgroundSrc) {
        this.renderer.setStyle(image, 'background-image', `url(${image.dataset.backgroundSrc})`);
        this.renderer.removeAttribute(image, 'data-background-src');
      }
      // Stop observing the current target
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(image);
      }
    }
    static {
      this.ɵfac = function LazyLoadImagesDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LazyLoadImagesDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LazyLoadImagesDirective,
        selectors: [["", "lazy-load-images", ""]],
        inputs: {
          intersectionObserverConfig: [1, "lazy-load-images", "intersectionObserverConfig"]
        }
      });
    }
  }
  return LazyLoadImagesDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let PasswordShowDirective = /*#__PURE__*/(() => {
  let PasswordShowDirective = class PasswordShowDirective {
    constructor() {
      /**
       * Is text input visible now
       */
      this._passwordVisible = false;
      this._el = inject(ElementRef);
      this._renderer = inject(Renderer2);
      this._platform = inject(PlatformService);
    }
    ngAfterViewInit() {
      if (!this._platform.isBrowser) {
        return;
      }
      this._toggleEl = this._el.nativeElement.previousElementSibling;
      this._renderer.setStyle(this._toggleEl, 'pointer-events', 'all');
      this._renderer.setStyle(this._toggleEl, 'cursor', 'pointer');
      /**
       * Handle click on toggle el
       */
      fromEvent(this._toggleEl, 'click').pipe(untilDestroyed(this), tap(() => {
        if (this._passwordVisible) {
          this._hide();
        } else {
          this._show();
        }
      })).subscribe();
    }
    /**
     * Hide input text
     *
     * @private
     */
    _hide() {
      this._renderer.setAttribute(this._el.nativeElement, 'type', 'password');
      this._renderer.removeClass(this._toggleEl, 'active');
      this._passwordVisible = false;
    }
    /**
     * Show input text
     *
     * @private
     */
    _show() {
      this._renderer.setAttribute(this._el.nativeElement, 'type', 'text');
      this._renderer.addClass(this._toggleEl, 'active');
      this._passwordVisible = true;
    }
    static {
      this.ɵfac = function PasswordShowDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PasswordShowDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: PasswordShowDirective,
        selectors: [["", "PasswordShow", ""]]
      });
    }
  };
  PasswordShowDirective = __decorate([UntilDestroy()], PasswordShowDirective);
  return PasswordShowDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SetAutofilledDirective = /*#__PURE__*/(() => {
  class SetAutofilledDirective {
    constructor() {
      this._el = inject(ElementRef);
      this._control = inject(NgControl);
      this._platform = inject(PlatformService);
    }
    ngAfterViewInit() {
      if (this._platform.isBrowser && this._el.nativeElement) {
        this._el.nativeElement.addEventListener('change', () => {
          setTimeout(() => {
            if (this._control && !this._control?.control?.value) {
              this._control?.control?.setValue(this._el.nativeElement.value);
            }
          }, 250);
        });
      }
    }
    static {
      this.ɵfac = function SetAutofilledDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SetAutofilledDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: SetAutofilledDirective,
        selectors: [["", "SetAutofilled", ""]]
      });
    }
  }
  return SetAutofilledDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This is a TypeScript directive that conditionally renders a template based on the environment parameter.
 *
 */
let BaseStageOnlyDirective = /*#__PURE__*/(() => {
  class BaseStageOnlyDirective {
    constructor() {
      this._viewContainer = inject(ViewContainerRef);
      this._templateRef = inject(TemplateRef);
      this.StageOnly = input('', ...(ngDevMode ? [{
        debugName: "StageOnly"
      }] : []));
    }
    ngOnInit() {
      const StageOnly = this.StageOnly();
      if (!StageOnly ? !this.environment.production : this.environment[StageOnly]) {
        this._viewContainer.createEmbeddedView(this._templateRef);
      } else {
        this._viewContainer.clear();
      }
    }
    static {
      this.ɵfac = function BaseStageOnlyDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BaseStageOnlyDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: BaseStageOnlyDirective,
        selectors: [["", "StageOnly", ""]],
        inputs: {
          StageOnly: [1, "StageOnly"]
        }
      });
    }
  }
  return BaseStageOnlyDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DebounceClickDirective = /*#__PURE__*/(() => {
  class DebounceClickDirective {
    constructor() {
      this.debounceClick = output();
      this.clicks = new Subject();
    }
    ngOnInit() {
      this.subscription = this.clicks.pipe(debounceTime(100)).subscribe(e => this.debounceClick.emit(e));
    }
    ngOnDestroy() {
      this.subscription.unsubscribe();
    }
    clickEvent(event) {
      this.clicks.next(event);
    }
    static {
      this.ɵfac = function DebounceClickDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DebounceClickDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DebounceClickDirective,
        selectors: [["", "appDebounceClick", ""]],
        hostBindings: function DebounceClickDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function DebounceClickDirective_click_HostBindingHandler($event) {
              return ctx.clickEvent($event);
            });
          }
        },
        outputs: {
          debounceClick: "debounceClick"
        }
      });
    }
  }
  return DebounceClickDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DobMaskDirective = /*#__PURE__*/(() => {
  let DobMaskDirective = class DobMaskDirective {
    constructor() {
      this._control = inject(NgControl);
    }
    ngOnInit() {
      if (this._control && this._control.valueChanges) {
        this._control.valueChanges.pipe(untilDestroyed(this), map(value => this._filterCharacters(value)), map(value => this._compareValues(value)), map(value => this._mask(value)), tap(value => {
          this._lastValue = value;
          if (this._control.control) {
            this._control.control.setValue(value, {
              emitEvent: false
            });
          }
        })).subscribe();
      }
    }
    /**
     * Remove all invalid characters
     *
     * @param value
     * @private
     */
    _filterCharacters(value) {
      return value ? value.split('').filter(char => /[0-9]/.test(char) || char === '/').join('') : '';
    }
    /**
     * Compare previous and current value for correct deleting
     *
     * @param current
     * @private
     */
    _compareValues(current) {
      if (this._lastValue && current.length < this._lastValue.length && current[current.length - 1] !== this._lastValue[this._lastValue.length - 1] && this._lastValue[this._lastValue.length - 1] === '/') {
        return current.split('').splice(0, current.length - 1).join('');
      }
      return current;
    }
    /**
     * Mask value
     *
     * @param value
     * @private
     */
    _mask(value) {
      const slashCount = value.split('').filter(char => char === '/').length;
      if (value.length === 2 && slashCount < 2) {
        value = value + '/';
      }
      if (value.length === 5 && slashCount < 2) {
        value = value + '/';
      }
      if (value.length > 10) {
        value = value.split('').splice(0, 10).join('');
      }
      return value;
    }
    static {
      this.ɵfac = function DobMaskDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DobMaskDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DobMaskDirective,
        selectors: [["", "appDobMask", ""]]
      });
    }
  };
  DobMaskDirective = __decorate([UntilDestroy()], DobMaskDirective);
  return DobMaskDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DomChangeDirective = /*#__PURE__*/(() => {
  class DomChangeDirective {
    constructor() {
      this.elementRef = inject(ElementRef);
      this.domChange = output();
    }
    ngOnInit() {
      const element = this.elementRef.nativeElement;
      this.changes = new MutationObserver(mutations => {
        mutations.forEach(mutation => this.domChange.emit(mutation));
      });
      this.changes.observe(element, {
        attributes: true,
        childList: true,
        characterData: true
      });
    }
    ngOnDestroy() {
      this.changes.disconnect();
    }
    static {
      this.ɵfac = function DomChangeDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DomChangeDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DomChangeDirective,
        selectors: [["", "domChange", ""]],
        outputs: {
          domChange: "domChange"
        }
      });
    }
  }
  return DomChangeDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ElementOnloadDirective = /*#__PURE__*/(() => {
  class ElementOnloadDirective {
    constructor() {
      this.elementLoaded = output();
      this.el = inject(ElementRef);
    }
    ngAfterViewInit() {
      this.elementLoaded.emit(this.el.nativeElement);
    }
    static {
      this.ɵfac = function ElementOnloadDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ElementOnloadDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ElementOnloadDirective,
        selectors: [["", "elementOnLoad", ""]],
        outputs: {
          elementLoaded: "elementLoaded"
        }
      });
    }
  }
  return ElementOnloadDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Memoize invalid values to prevent submit invalid value (when error comes from backend)
 */
let MemoizeInvalidDirective = /*#__PURE__*/(() => {
  class MemoizeInvalidDirective {
    constructor() {
      this._control = inject(NgControl);
    }
    ngAfterViewInit() {
      if (!this._control) {
        throw new Error('Works only with form controls');
      }
      const errors = new Map();
      this._control?.statusChanges?.pipe(filter(() => this._control.invalid), switchMap(() => of(this._control.value)), filter(value => !errors.has(value)), distinctUntilChanged(), tap(value => errors.set(value, this._control.errors))).subscribe();
      this._control?.valueChanges?.pipe(filter(value => errors.has(value)), tap(value => this._control.control?.setErrors(errors.get(value)))).subscribe();
    }
    static {
      this.ɵfac = function MemoizeInvalidDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MemoizeInvalidDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MemoizeInvalidDirective,
        selectors: [["", "memoizeInvalid", ""]]
      });
    }
  }
  return MemoizeInvalidDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NoEmojiDirective = /*#__PURE__*/(() => {
  class NoEmojiDirective {
    onInput(event) {
      const input = event.target;
      const sanitizedValue = input.value.replace(/(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g, '');
      input.value = sanitizedValue;
    }
    static {
      this.ɵfac = function NoEmojiDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NoEmojiDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NoEmojiDirective,
        selectors: [["", "noEmoji", ""]],
        hostBindings: function NoEmojiDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("input", function NoEmojiDirective_input_HostBindingHandler($event) {
              return ctx.onInput($event);
            });
          }
        }
      });
    }
  }
  return NoEmojiDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NoSpaceDirective = /*#__PURE__*/(() => {
  class NoSpaceDirective {
    constructor() {}
    onKeyDown(event) {
      if (event.key === ' ') {
        event.preventDefault();
      }
    }
    static {
      this.ɵfac = function NoSpaceDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NoSpaceDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NoSpaceDirective,
        selectors: [["", "appNoSpace", ""]],
        hostBindings: function NoSpaceDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("keydown", function NoSpaceDirective_keydown_HostBindingHandler($event) {
              return ctx.onKeyDown($event);
            });
          }
        }
      });
    }
  }
  return NoSpaceDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NumberOnlyDirective = /*#__PURE__*/(() => {
  class NumberOnlyDirective {
    constructor() {
      this._el = inject(ElementRef);
      this._renderer = inject(Renderer2);
    }
    ngOnInit() {
      this._renderer.setAttribute(this._el.nativeElement, 'type', 'text');
      this._renderer.setAttribute(this._el.nativeElement, 'inputmode', 'numeric');
      this._renderer.setAttribute(this._el.nativeElement, 'pattern', '[0-9]*');
    }
    static {
      this.ɵfac = function NumberOnlyDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NumberOnlyDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NumberOnlyDirective,
        selectors: [["", "NumberOnly", ""]]
      });
    }
  }
  return NumberOnlyDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let RouterLinkDirective = /*#__PURE__*/(() => {
  class RouterLinkDirective {
    constructor() {
      this.routerLink = input.required(...(ngDevMode ? [{
        debugName: "routerLink"
      }] : []));
    }
    ngOnChanges(changes) {
      if (changes['routerLink'] && this.linkActives && this.linkActives.first) {
        this.linkActives.first.ngOnChanges(null);
      }
    }
    ngAfterContentInit() {
      this.linkActives.changes.subscribe(_ => this.linkActives.first.ngOnChanges(null));
    }
    static {
      this.ɵfac = function RouterLinkDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RouterLinkDirective)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: RouterLinkDirective,
        selectors: [["", "routerLink", ""]],
        contentQueries: function RouterLinkDirective_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, RouterLinkActive, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.linkActives = _t);
          }
        },
        inputs: {
          routerLink: [1, "routerLink"]
        },
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return RouterLinkDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ObserveVisibilityDirective = /*#__PURE__*/(() => {
  class ObserveVisibilityDirective {
    constructor(el) {
      this.el = el;
      this._platform = inject(PlatformService);
      this.onVisible = output();
      this.onGone = output();
      this.threshold = input(0.1, ...(ngDevMode ? [{
        debugName: "threshold"
      }] : []));
      this.once = input(true, ...(ngDevMode ? [{
        debugName: "once"
      }] : []));
      this._isVisible = false;
    }
    ngAfterViewInit() {
      if (this._platform.isBrowser) {
        this._observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            this._isVisible = true;
            this.onVisible.emit();
            if (this.once()) {
              this._observer.disconnect();
            }
          } else {
            if (this._isVisible) {
              this._isVisible = false;
              this.onGone.emit();
            }
          }
        }, {
          threshold: this.threshold()
        });
        this._observer.observe(this.el.nativeElement);
      } else {
        this.onVisible.emit();
      }
    }
    ngOnDestroy() {
      if (this._platform.isBrowser) {
        this._observer.disconnect();
      }
    }
    static {
      this.ɵfac = function ObserveVisibilityDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ObserveVisibilityDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: ObserveVisibilityDirective,
        selectors: [["", "appObserveVisibility", ""]],
        inputs: {
          threshold: [1, "threshold"],
          once: [1, "once"]
        },
        outputs: {
          onVisible: "onVisible",
          onGone: "onGone"
        }
      });
    }
  }
  return ObserveVisibilityDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { AccordionDirective, AppShellMobileDirective, AppShellNoMobileDirective, AppShellNoSSRDirective, AppShellRenderSSRDirective, BaseStageOnlyDirective, ClassLoadedDirective, ClickOutsideDirective, CropTextDirective, DebounceClickDirective, DefaultSourceDirective, DeleteNotFoundDirective, DobMaskDirective, DomChangeDirective, ElementOnloadDirective, FilterInputDirective, FitSvgDirective, ImgDimensionDirective, InputNumberDirective, LazyLoadImagesDirective, LeadingStringDirective, MemoizeInvalidDirective, NoEmojiDirective, NoSpaceDirective, NumberOnlyDirective, ObserveVisibilityDirective, PasswordShowDirective, RouterLinkDirective, SetAutofilledDirective };
